import React, { useState, useEffect, useCallback } from "react";
import { Table, Button, Modal, Space, Popconfirm, message, Tag } from "antd";
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  MenuOutlined,
  EyeOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import {
  DndContext,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import FormComp from "./Form";
import Preview from "./Preview";
import { useUser } from "../../../UserContext";

const DraggableRow = ({ children, ...props }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: props["data-row-key"] });

  const style = {
    ...props.style,
    transform: CSS.Transform.toString(transform),
    transition,
    cursor: "move",
    ...(isDragging ? { background: "#fafafa", opacity: 0.6 } : {}),
  };

  return (
    <tr
      {...props}
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
    >
      {children}
    </tr>
  );
};

const QuestionsList = () => {
  const [preguntas, setPreguntas] = useState([]);
  const [modalVisible, setModalVisible] = useState(false);
  const [previewModalVisible, setPreviewModalVisible] = useState(false);
  const [selectedPregunta, setSelectedPregunta] = useState(null);
  const [loading, setLoading] = useState(false);
  const [previewRefreshKey, setPreviewRefreshKey] = useState(0);
  const [previewLoading, setPreviewLoading] = useState(false);
  const API_URL = process.env.REACT_APP_API_URL;
  const { user: currentUser } = useUser();

  // Función de permisos
  // Función de permisos para módulo Transporte
  const hasPermission = useCallback(
    (permission) => {
      const module = currentUser?.modulos?.find((m) => m.key === "Transporte");
      if (!module || module.estado !== "disponible") return false;
      return currentUser?.modulosPermisos?.some(
        (mp) => mp.modulo === "Transporte" && mp.permiso === permission
      );
    },
    [currentUser]
  );

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } })
  );

  const fetchPreguntas = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("accessToken");
      const res = await fetch(`${API_URL}/pre-operaciones`, {
        headers: { Authorization: token ? `Bearer ${token}` : "" },
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({ message: res.statusText }));
        message.error(`Error al cargar preguntas: ${err.error || err.message}`);
        return;
      }
      const data = await res.json();
      const sortedData = Array.isArray(data)
        ? [...data].sort((a, b) => a.orden - b.orden)
        : [];
      setPreguntas(sortedData);
    } catch (error) {
      console.error("Error al cargar preguntas:", error);
      message.error("No se pudieron cargar las preguntas");
    } finally {
      setLoading(false);
    }
  }, [API_URL]);

  useEffect(() => {
    fetchPreguntas();
  }, [fetchPreguntas]);

  const deletePregunta = async (id) => {
    try {
      const token = localStorage.getItem("accessToken");
      await fetch(`${API_URL}/pre-operaciones/${id}`, {
        method: "DELETE",
        headers: { Authorization: token ? `Bearer ${token}` : "" },
      });
      message.success("Pregunta eliminada.");
      fetchPreguntas();
    } catch (err) {
      console.error("QuestionsList.jsx: error fetch", err);
      message.error("No se pudo eliminar.");
    }
  };

  const openModal = (pregunta = null) => {
    setSelectedPregunta(pregunta);
    setModalVisible(true);
  };

  const closeModal = () => {
    setSelectedPregunta(null);
    setModalVisible(false);
  };

  const openPreviewModal = () => {
    setPreviewLoading(true);
    setPreviewModalVisible(true);
    setTimeout(() => setPreviewRefreshKey((prev) => prev + 1), 100);
  };

  const closePreviewModal = () => setPreviewModalVisible(false);

  const handlePreviewDataLoaded = () => setPreviewLoading(false);

  const handleRefreshPreview = () => {
    setPreviewLoading(true);
    setTimeout(() => setPreviewRefreshKey((prev) => prev + 1), 100);
  };

  const handleDragEnd = async ({ active, over }) => {
    if (!active || !over || active.id === over.id) return;
    setLoading(true);
    try {
      const items = [...preguntas];
      const from = items.findIndex((item) => item.id === active.id);
      const to = items.findIndex((item) => item.id === over.id);
      const [moved] = items.splice(from, 1);
      items.splice(to, 0, moved);
      setPreguntas(items);
      const actual = items.map((item, idx) => ({ id: item.id, orden: idx }));
      const token = localStorage.getItem("accessToken");
      const res = await fetch(`${API_URL}/pre-operaciones/reordenar`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: token ? `Bearer ${token}` : "",
        },
        body: JSON.stringify({ items: actual }),
      });
      if (!res.ok) throw new Error();
      message.success("Orden actualizado correctamente");
    } catch (error) {
      console.error("Error al actualizar el orden:", error);
      message.error("No se pudo actualizar el orden");
      fetchPreguntas();
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    {
      title: "Orden",
      key: "sort",
      width: 30,
      className: "drag-visible",
      render: () => <MenuOutlined style={{ cursor: "grab", color: "#999" }} />,
    },
    { title: "Pregunta", dataIndex: "titulo", key: "titulo" },
    {
      title: "Tipo",
      dataIndex: "tipoRespuesta",
      key: "tipoRespuesta",
      render: (tipo) => {
        if (tipo === "texto") return <Tag color="blue">Texto</Tag>;
        if (tipo === "numerico") return <Tag color="orange">Numérico</Tag>;
        if (tipo === "checklist_simple")
          return <Tag color="green">Checklist Simple</Tag>;
        if (tipo === "checklist_multiple")
          return <Tag color="purple">Checklist Múltiple</Tag>;
        return tipo;
      },
    },
    {
      title: "Acciones",
      key: "acciones",
      render: (_, record) => (
        <Space>
          {hasPermission("EDITAR_T") && (
            <Button icon={<EditOutlined />} onClick={() => openModal(record)}>
              Editar
            </Button>
          )}

          {hasPermission("ELIMINAR_T") && (
            <Popconfirm
              title="¿Seguro que deseas eliminar esta pregunta?"
              onConfirm={() => deletePregunta(record.id)}
              okText="Sí"
              cancelText="No"
            >
              <Button danger icon={<DeleteOutlined />}>
                Eliminar
              </Button>
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  return (
    <>
      <div style={{ marginBottom: 16, textAlign: "center" }}>
        {hasPermission("VISUALIZAR_T") && (
          <Button
            icon={<EyeOutlined />}
            onClick={openPreviewModal}
            style={{ marginRight: 8 }}
          >
            Vista Previa
          </Button>
        )}

        {hasPermission("CREAR_T") && (
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => openModal()}
          >
            Agregar Pregunta
          </Button>
        )}
      </div>

      <DndContext sensors={sensors} onDragEnd={handleDragEnd}>
        <SortableContext
          items={preguntas.map((p) => p.id)}
          strategy={verticalListSortingStrategy}
        >
          <Table
            loading={loading}
            columns={columns}
            dataSource={preguntas}
            rowKey="id"
            pagination={false}
            components={{ body: { row: DraggableRow } }}
          />
        </SortableContext>
      </DndContext>

      {/* Proteger diálogos con permisos */}
      {modalVisible &&
        hasPermission(selectedPregunta ? "EDITAR_T" : "CREAR_T") && (
          <Modal
            title={selectedPregunta ? "Editar Pregunta" : "Nueva Pregunta"}
            open={modalVisible}
            onCancel={closeModal}
            footer={null}
            destroyOnHidden
          >
            <FormComp
              initialValues={selectedPregunta}
              onSuccess={() => {
                fetchPreguntas();
                closeModal();
                if (previewModalVisible) handleRefreshPreview();
              }}
              closeModal={closeModal}
            />
          </Modal>
        )}

      {previewModalVisible && hasPermission("VISUALIZAR_T") && (
        <Modal
          title={
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <span>Vista Previa del Formulario</span>
              <Button
                icon={<ReloadOutlined />}
                onClick={handleRefreshPreview}
                loading={previewLoading}
              >
                Actualizar
              </Button>
            </div>
          }
          open={previewModalVisible}
          onCancel={closePreviewModal}
          width={800}
          footer={[
            <Button key="close" onClick={closePreviewModal}>
              Cerrar
            </Button>,
          ]}
        >
          <Preview
            refreshKey={previewRefreshKey}
            onDataLoaded={handlePreviewDataLoaded}
          />
        </Modal>
      )}
    </>
  );
};

export default QuestionsList;
